import { User, RegisterUserData} from '../types';
import { jwtDecode } from "jwt-decode";

import api from '../../../lib/api/api';

// Constants
const TOKEN_KEY = 'token';

interface DecodedToken {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  exp: number;
}

// Authentication service
export const authService = {
  // Login user
  login: async (email: string, password: string): Promise<string> => {
    const response = await api.post('/auth/login', {
      email,
      password
    });

    const { token, user } = response.data as { token: string; user?: object };

    // Store token
    localStorage.setItem(TOKEN_KEY, token);

    // Store user data
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    }

    return token;
  },

  // Register new user
  register: async (userData: RegisterUserData): Promise<string> => {
    const response = await api.post('/auth/signup', userData);

    const { token, user } = response.data as { token: string; user?: object };

    // Store token
    localStorage.setItem(TOKEN_KEY, token);

    // Store user data
    if (user) {
      localStorage.setItem('user', JSON.stringify(user));
    }

    return token;
  },

  // Logout user
  logout: (): void => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem('user');
  },

  // Get current user information from local storage or token
  getCurrentUser: async (): Promise<User> => {
    const token = localStorage.getItem(TOKEN_KEY);

    if (!token) {
      throw new Error('No authentication token found');
    }

    // First try to get user from localStorage
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      return JSON.parse(storedUser);
    }

    // If no stored user, try to decode the token
    try {
      const decoded = jwtDecode<DecodedToken>(token);

      // Check if token is expired
      const currentTime = Date.now() / 1000;
      if (decoded.exp < currentTime) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem('user');
        throw new Error('Token expired');
      }

      // Create user object from decoded token
      const user: User = {
        id: decoded.userId,
        email: decoded.email,
        name: `${decoded.firstName} ${decoded.lastName}`,
        role: 'user', // Default role
        firstName: decoded.firstName,
        lastName: decoded.lastName
      };

      // Store user data for future use
      localStorage.setItem('user', JSON.stringify(user));

      return user;
    } catch {
      throw new Error('Invalid token format');
    }
  },

  // Check if user is authenticated (token exists)
  isAuthenticated: (): boolean => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return false;

    try {
      // Verify token is not expired
      const decoded = jwtDecode<DecodedToken>(token);
      const currentTime = Date.now() / 1000;
      return decoded.exp > currentTime;
    } catch {
      return false;
    }
  },

  // Get token
  getToken: (): string | null => {
    return localStorage.getItem(TOKEN_KEY);
  },

  // Refresh token (implement if your backend supports it)
  refreshToken: async (): Promise<string | null> => {
    try {
      const token = localStorage.getItem(TOKEN_KEY);

      if (!token) {
        return null;
      }

      // For now, just check if token is valid and return it
      if (authService.isAuthenticated()) {
        return token;
      } else {
        // Clear invalid token
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem('user');
        return null;
      }
    } catch {
      return null;
    }
  }
};
